import { ShareTicketBody } from "@mg/schemas/src/christo/catalyst";
import {
  ArrowLeft,
  ArrowRight,
  CaretDown,
  CaretRight,
  UploadSimple,
  UserCircle,
  Link as LinkIcon,
  ShareNetwork,
  HourglassLow,
} from "@phosphor-icons/react";
import {
  Button,
  Card,
  Flex,
  Grid,
  Heading,
  Link,
  Text,
} from "@radix-ui/themes";
import {
  // eslint-disable-next-line import/named
  Link as NavLink,
} from "@tanstack/react-router";
import cx from "classnames";
import { usePostHog } from "posthog-js/react";
import React, { useMemo, useState, useEffect } from "react";

import CreateVersionDialog, { Asset } from "./dialogs/CreateRevisionDialog";
import { RenameTicketDialog } from "./dialogs/RenameTicket";
import MissingFontsIndicator from "./MissingFontsIndicator";
import VersionSelect from "./VersionSelect";

import { AuthTooltip } from "../../../../../components/AuthTooltip";
import { Expandable } from "../../../../../components/Expandable";
import { useUI } from "../../../../../contexts/ui";
import { useAnalytics } from "../../../../../utils/analytics";
import { canAccessPunttDashboard } from "../../../../../utils/auth";
import { isNil } from "../../../../../utils/fp";
import { useAppDispatch, useAppSelector } from "../../../../../utils/hooks";
import { downloadAsset } from "../../../../../utils/http";
import {
  useDeleteRevisionMutation,
  useDeleteTicketMutation,
  useDownloadTicketLink,
  useEditProjectMutation,
  useRevisionMutation,
  useShareTicket,
  useShareTicketLink,
} from "../../../../../utils/queries/projects";
import { setTicket } from "../../../../../utils/slices/ticket";
import { workflowsRoute } from "../../../../workflows/route";
import {
  Breadcrumbs,
  TruncateWithTooltip,
} from "../../../components/Breadcrumbs";
import { DeleteTicketDialog } from "../../../components/DeleteTicketDialog";
import {
  ShareDialog,
  type ShareDialogProps,
} from "../../../components/dialogs/ShareDialog";
import { ticketsRoute } from "../../../route";
import { VersionManagerDialog } from "../../components/dialogs/VersionManagerDialog";
import { ticketRoute } from "../route";

// Utility function to format hours into "X days Y hrs" format
function formatTimeSaved(hours: number): string {
  if (hours < 24) {
    return `${hours} hrs`;
  }

  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;

  if (remainingHours === 0) {
    return `${days} day${days > 1 ? "s" : ""}`;
  }

  return `${days} day${days > 1 ? "s" : ""} ${remainingHours} hrs`;
}

// Utility function to format seconds into "X mins Y secs" format
function formatTimeReviewTook(seconds: number): string {
  if (seconds < 60) {
    return `${seconds} secs`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (remainingSeconds === 0) {
    return `${minutes} min${minutes > 1 ? "s" : ""}`;
  }

  return `${minutes} min${minutes > 1 ? "s" : ""} ${remainingSeconds} secs`;
}

export default function TicketHeader() {
  // Router methods
  const { ticketId } = ticketRoute.useParams();
  const { version: versionIndex, view } = ticketRoute.useSearch();
  const { tickets } = ticketRoute.useLoaderData() as {
    tickets: Ticket[] | undefined;
  };
  const navigate = ticketRoute.useNavigate();
  const { notify } = useUI();
  const posthog = usePostHog();

  // Slices
  const dispatch = useAppDispatch();
  const totalVersions =
    useAppSelector((state) => state.ticket.value?.totalRevisions) ?? 0;
  const { value: ticket, comments } = useAppSelector((state) => state.ticket);
  const ticketTitle =
    ticketId == "editabledoc" // demo purposes only
      ? "Data Processing Agreement"
      : ticketId == "editabledoc2"
        ? "Coca-Cola Joke List"
        : (ticket?.title ?? "Untitled ticket");
  const versions = useAppSelector((state) => state.ticket.revisions);
  const reversedVersionIndex = useAppSelector(
    (state) => state.ticket.reversedVersionIndex,
  );
  const user = useAppSelector((state) => state.auth.value);

  // Queries and mutations
  const renameTicketMutation = useEditProjectMutation(ticketId);
  const deleteRevisionMutation = useDeleteRevisionMutation(ticketId);
  const createVersionMutation = useRevisionMutation();
  const ticketShareLinkMutation = useShareTicketLink();
  const shareTicketMutation = useShareTicket();
  const deleteTicketMutation = useDeleteTicketMutation();
  const downloadTicketMutation = useDownloadTicketLink();

  // Analytics
  const analytics = useAnalytics(`root_ticket_${ticketId}`);

  // Local state
  const [versionManagerDialogOpen, setVersionManagerDialogOpen] =
    useState(false);
  const [createVersionDialogOpen, setCreateVersionDialogOpen] = useState(false);
  const [renameTicketDialogOpen, setRenameTicketDialogOpen] = useState(false);
  const [shareItem, setShareItem] = useState<ShareDialogProps["item"]>();
  const [shareLink, setShareLink] = useState<string>();
  const [deletingTicketId, setDeletingTicketId] = useState<
    string | undefined
  >();
  const currentVersion = useMemo(() => {
    return versions[reversedVersionIndex!];
  }, [reversedVersionIndex, versions]);

  // Effect chain would go here
  useEffect(() => {
    // Only generate share link if user is not a guest
    if (user && !user.referredBy) {
      // Generate share link on page load and when ticket ID changes
      ticketShareLinkMutation.mutate(
        { ticketId, version: "all" },
        {
          onSuccess(data) {
            if (isNil(data)) {
              throw new Error("An unknown error occurred");
            }

            let { url } = data;
            if (versionIndex) {
              url = `${url}&version=${versionIndex}`;
            }

            setShareLink(url);
          },
        },
      );
    }
  }, [ticketId, versionIndex, user]);

  /////
  // No hooks beyond this point
  /////

  function handleRenameTicket(ticketTitle: string) {
    const startTime = performance.now();

    return renameTicketMutation.mutateAsync(
      { title: ticketTitle, _id: ticketId },
      {
        onSuccess(data) {
          const endTime = performance.now();

          analytics.capture("ticket_renamed", {
            ticketTitle: ticketTitle,
            mutationDuration: endTime - startTime,
            durationSincePageLoad:
              endTime - (analytics.startTimeRef?.current ?? 0),
          });

          setRenameTicketDialogOpen(false);

          if (isNil(data)) return;

          dispatch(setTicket(data));
        },
      },
    );
  }

  function handleCreateVersion(assets: Asset[]) {
    const firstAsset = assets[0];
    const { source } = firstAsset;

    createVersionMutation.mutate(
      {
        ticketId,
        payload: {
          reviewFiles: assets,
          documentImportType: source.slice(source.lastIndexOf(".") + 1),
        },
      },
      {
        onSuccess(data) {
          setCreateVersionDialogOpen(false);
          // FIXME: this is not guaranteed to be the board the user just uploaded if
          // two people uploaded versions at the same time.
          const newBoard = data.boards[data.boards.length - 1];

          analytics.capture("created_revision", { boardId: newBoard._id });

          // Cleverly call the route's `loader` function, which will
          // automatically select the newest version index and append it to the
          // URL bar.
          navigate({});
        },
      },
    );
  }

  function handleShareTicket(
    ticketId: string,
    emails: string[],
    shareVersion: ShareTicketBody["version"],
  ) {
    const start = performance.now();

    shareTicketMutation.mutate(
      { ticketId, emails, version: shareVersion },
      {
        onSuccess() {
          analytics.capture("generate_share_ticket_link", {
            ticketId,
            recipientCount: emails.length,
            shareVersion,
            duration: performance.now() - start,
          });
          setShareLink(undefined);
          setShareItem(undefined);
          notify({
            title: "Ticket shared",
            message: `Email has been sent to ${emails.length} recipient(s)`,
          });
        },
      },
    );
  }

  function handleShareRequest() {
    setShareLink(undefined);
    setShareItem({ type: "ticket", id: ticketId });
    ticketShareLinkMutation.mutate(
      { ticketId, version: "all" },
      {
        onSuccess(data) {
          if (isNil(data)) {
            throw new Error("An unknown error occurred");
          }

          setShareLink(data.url);
        },
      },
    );
  }

  function handleDownloadTicket(ticketId: string) {
    const startTime = performance.now();
    downloadTicketMutation.mutate(
      { ticketId, boardId: currentVersion._id },
      {
        onSuccess(data) {
          posthog.capture("download_ticket", {
            ticket_id: ticketId,
            ticket_name: ticketTitle,
            data_length: data?.url.length,
            request_duration: performance.now() - startTime,
          });
          if (data) {
            downloadAsset(data.url);
          }
        },
      },
    );
  }

  function handleCopyLink() {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink);
    notify({
      title: "Share link copied to clipboard",
    });
  }

  const versionManagerList =
    versions.map((board, i) => ({
      _id: board._id,
      title: board.name ?? `Version ${i + 1}`,
      thumbnail: board.thumbnails[0] ?? board.screenshotUrl,
    })) ?? [];

  interface Ticket {
    _id: string;
    title: string;
  }

  const currentTicketIndex = (tickets as Ticket[] | undefined)?.findIndex(
    (t: Ticket) => t._id === ticketId,
  );
  const isWorkflow = view === "workflow";
  const hasAIComments = useMemo(() => {
    return comments?.some((c) => c.isAI);
  }, [comments?.length > 0]);

  return (
    <>
      {/* Put dialogs here, please */}
      <VersionManagerDialog
        open={versionManagerDialogOpen}
        onOpenChange={setVersionManagerDialogOpen}
        onDeleteVersion={(revisionId, index) =>
          deleteRevisionMutation.mutate(revisionId, {
            onSuccess(data) {
              analytics.capture("delete_version", {
                ticketId,
                versionId: revisionId,
              });
              if (isNil(data)) return;

              const { boards } = data;

              if (!boards.length) {
                setVersionManagerDialogOpen(false);
                navigate({
                  search({ version: _version, ...rest }) {
                    return rest;
                  },
                });
              }

              navigate({
                search(prev) {
                  return {
                    ...prev,
                    version:
                      !isNil(prev.version) && prev.version >= index
                        ? prev.version - 1
                        : prev.version,
                  };
                },
              });
            },
          })
        }
        revisions={versionManagerList}
        isPending={deleteRevisionMutation.isPending}
        deletingId={deleteRevisionMutation.variables}
      />
      <CreateVersionDialog
        isPending={createVersionMutation.isPending}
        open={createVersionDialogOpen}
        onOpenChange={setCreateVersionDialogOpen}
        onSubmitAssets={handleCreateVersion}
      />
      <RenameTicketDialog
        open={renameTicketDialogOpen}
        onOpenChange={setRenameTicketDialogOpen}
        isPending={renameTicketMutation.isPending}
        onSubmit={handleRenameTicket}
        value={ticketTitle}
      />
      <ShareDialog
        isPending={shareTicketMutation.isPending}
        item={shareItem}
        latestVersionNumber={totalVersions}
        onCancel={() => setShareItem(undefined)}
        onShare={(emails, shareVersion) => {
          if (isNil(shareLink)) return;

          handleShareTicket(ticketId, emails, shareVersion);
        }}
        onVersionChange={(version) =>
          ticketShareLinkMutation.mutate(
            { ticketId: shareItem!.id, version },
            {
              onSuccess(data) {
                if (isNil(data)) {
                  throw new Error("An unknown error occurred");
                }

                let { url } = data;

                if (version === "all") {
                  url = `${url}&version=${versionIndex}`;
                }

                setShareLink(url);
              },
            },
          )
        }
        shareLink={shareLink}
        shareLinkPending={ticketShareLinkMutation.isPending}
      />
      <DeleteTicketDialog
        isPending={deleteRevisionMutation.isPending}
        ticketId={deletingTicketId}
        onCancel={() => setDeletingTicketId(undefined)}
        onDelete={() => {
          deleteTicketMutation.mutate(
            { ticketId },
            {
              onSuccess() {
                navigate({
                  to: ticketsRoute.to,
                  search: (prev) => {
                    const searchParams = { ...prev };
                    if (ticket?.folder) {
                      // @ts-expect-error TS2339: Open ended searchParams in case we change them
                      searchParams.folderId = ticket.folder;
                    }
                    return searchParams;
                  },
                });
              },
            },
          );
        }}
      />
      {/* Main DOM goes here */}
      <Grid
        gap="4"
        className="col-span-1 row-span-1 bg-base-white px-7 py-4 shadow"
        data-testid="ticket-header-container"
      >
        <Flex
          gap="3"
          align="center"
          justify="between"
          data-testid="ticket-header_ticket-manager-row"
        >
          <Flex gap="3" align="center" className="min-w-0">
            <Heading
              as="h1"
              size="5"
              weight="bold"
              className="dali-font-national2"
            >
              <span className="dali-font-national2">
                <Link asChild data-auth-trigger="projects-link">
                  <NavLink
                    to={!isWorkflow ? ticketsRoute.to : workflowsRoute.to}
                    search={({ folderId: _folderId, ...prev }) => prev}
                    className="puntt-link"
                  >
                    {isWorkflow ? "Workflows" : "Projects"}
                  </NavLink>
                </Link>
              </span>
            </Heading>
            <Breadcrumbs
              ticketTitle={
                <>
                  <CaretRight
                    className="shrink-0"
                    style={{ background: "none" }}
                  />
                  <Expandable
                    maxLines={3}
                    size="1"
                    buttonText={<CaretDown className="shrink-0" />}
                  >
                    <Flex
                      align="center"
                      gap="2"
                      className="min-w-0 overflow-hidden"
                    >
                      <Text
                        size="4"
                        className="w-full min-w-0 text-[20px] font-normal leading-[28px] tracking-[-0.08px]"
                      >
                        <TruncateWithTooltip>{ticketTitle}</TruncateWithTooltip>
                      </Text>
                    </Flex>
                  </Expandable>
                </>
              }
              onDeleteFolder={() => {
                setDeletingTicketId(ticketId);
              }}
              onManageVersions={() => setVersionManagerDialogOpen(true)}
              onRenameFolder={() => setRenameTicketDialogOpen(true)}
              onShareFolder={handleShareRequest}
              onDownloadFolder={() => handleDownloadTicket(ticketId)}
            />
          </Flex>

          <Flex
            gap="4"
            className={cx({
              hidden:
                isNil(tickets) ||
                isNil(user?.email) ||
                !canAccessPunttDashboard(),
            })}
          >
            <Button
              variant="outline"
              size="1"
              color="gray"
              disabled={
                currentTicketIndex === 0 ||
                (!isNil(tickets) && (tickets as Ticket[]).length === 1)
              }
              onClick={() =>
                navigate({
                  to: ticketRoute.to,
                  // @ts-expect-error TS18048: Button is disabled anyway, so
                  // it's fine
                  params: { ticketId: tickets?.[currentTicketIndex - 1]._id },
                })
              }
            >
              <ArrowLeft />
              Previous asset
            </Button>
            <Button
              variant="outline"
              size="1"
              color="gray"
              disabled={
                !isNil(tickets) &&
                (currentTicketIndex === (tickets as Ticket[]).length - 1 ||
                  (tickets as Ticket[]).length === 1)
              }
              onClick={() =>
                navigate({
                  to: ticketRoute.to,
                  // @ts-expect-error TS18048: Button is disabled anyway, so
                  // it's fine
                  params: { ticketId: tickets?.[currentTicketIndex + 1]._id },
                })
              }
            >
              Next asset
              <ArrowRight />
            </Button>
          </Flex>
          {hasAIComments &&
            ticket?.revisionBoards[0]?.thumbnails?.length &&
            ticket?.revisionBoards[0]?.timeSaved &&
            ticket?.revisionBoards[0]?.timeReviewTook && (
              <Card className="absolute right-80 top-28 z-[2000] flex items-center gap-2 p-2">
                <HourglassLow size={36} className="text-puntt-neutral-gray-6" />
                <Grid>
                  <Text size="3" weight="regular">
                    You saved{" "}
                    {formatTimeSaved(ticket?.revisionBoards[0]?.timeSaved ?? 0)}{" "}
                    with this review.
                  </Text>
                  <Flex gap="2">
                    <Text size="2" weight="regular">
                      Automated Review:{" "}
                      {formatTimeReviewTook(
                        ticket?.revisionBoards[0]?.timeReviewTook ?? 0,
                      )}
                    </Text>
                    <Text size="2" weight="regular">
                      Manual Review: {ticket?.revisionBoards[0]?.timeSaved} hrs
                    </Text>
                  </Flex>
                </Grid>
              </Card>
            )}
          <Button
            variant="outline"
            color="blue"
            size="1"
            className={cx({ hidden: !isNil(user?.email) })}
            data-auth-trigger="login-signup-button"
          >
            <UserCircle /> Log In or Sign Up
          </Button>
        </Flex>

        <Flex
          gap="2"
          align="center"
          data-testid="ticket-header_version-manager-row"
          className="w-full justify-between whitespace-nowrap"
        >
          <Flex gap="2" align="center">
            <VersionSelect />
            <MissingFontsIndicator />
            <AuthTooltip
              trigger="upload-version-cta"
              classNames="cursor-pointer"
            >
              <Button
                variant="outline"
                size="1"
                onClick={() => setCreateVersionDialogOpen(true)}
                data-testid="ticket-header_upload-version-cta"
                className="border-[#3C39EC] text-[#3C39EC]"
              >
                <UploadSimple /> Upload New Version
              </Button>
            </AuthTooltip>
            <div className="flex h-6 flex-row items-center overflow-hidden rounded-[4.5px] p-0">
              <AuthTooltip classNames="cursor-pointer">
                <Button
                  size="1"
                  variant="outline"
                  className="h-6 min-w-0 !rounded-none !rounded-l-[4.5px] border-r-0 border-[#3C39EC] px-2 text-[#3C39EC]"
                  disabled={!versions.length}
                  onClick={handleShareRequest}
                >
                  <ShareNetwork className="mr-1" /> Share
                </Button>
              </AuthTooltip>
              <AuthTooltip classNames="cursor-pointer">
                <Button
                  size="1"
                  variant="outline"
                  className="-ml-px h-6 min-w-0 !rounded-none !rounded-r-[4.5px] border-l-0 border-[#3C39EC] px-2 text-[#3C39EC]"
                  onClick={handleCopyLink}
                  disabled={!shareLink}
                  aria-label="Copy Share Link"
                >
                  <LinkIcon />
                </Button>
              </AuthTooltip>
            </div>
          </Flex>
        </Flex>
      </Grid>
    </>
  );
}
